<script setup>
// Botão CDC de chamada WAVOIP. Self-gate por `phone` + `currentUser.custom_wavoip_token`
// (D-06 / BUTTON-01/02 — Boolean() guard literal). Bloqueio assimétrico contra Twilio
// via `useCallsStore().hasActiveCall ?? false` (D-11 / BUTTON-05). Visual uniforme
// (icon `i-ph-whatsapp-logo`, size `sm`, slate, faded — D-08), tooltip dinâmico
// com 3 ramos (D-09), feedback de dialing via `is-loading` (D-13). Channel-agnostic:
// NÃO consulta o tipo de canal da conversa (D-12 / BUTTON-06). Origem: ADR 0007.
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useCallsStore } from 'dashboard/stores/calls';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { useWavoipCallsStore } from '../stores/wavoipCalls';
import { useWavoipCallSession } from '../composables/useWavoipCallSession';

const props = defineProps({
  phone: { type: String, default: '' },
  contactId: { type: [String, Number], default: null },
});

const { t } = useI18n();
const currentUser = useMapGetter('getCurrentUser');
const wavoipStore = useWavoipCallsStore();
const callsStore = useCallsStore();
const { startCall } = useWavoipCallSession();

// D-06 / BUTTON-01/02 — Boolean() guard literal (PITFALLS.md #5).
const shouldRender = computed(
  () => Boolean(props.phone) && Boolean(currentUser.value?.custom_wavoip_token)
);

const isWavoipBusy = computed(() => wavoipStore.status !== 'idle');
// D-11 / BUTTON-05 — `?? false` literal (PITFALLS.md #6).
const isTwilioBusy = computed(() => callsStore.hasActiveCall ?? false);
const isBusy = computed(() => isWavoipBusy.value || isTwilioBusy.value);
const isDialing = computed(() => wavoipStore.status === 'dialing');

// D-09 — 3 ramos i18n literais (evita o aviso @intlify/vue-i18n/no-dynamic-keys
// e mantém os IDs greppáveis no catálogo CDC).
const tooltipText = computed(() => {
  if (isWavoipBusy.value) return t('CDC.WAVOIP.button.disabled_busy_wavoip');
  if (isTwilioBusy.value) return t('CDC.WAVOIP.button.disabled_busy_twilio');
  return t('CDC.WAVOIP.button.tooltip');
});

const onClick = async () => {
  if (isBusy.value) return;
  await startCall(props.phone, props.contactId);
};
</script>

<template>
  <NextButton
    v-if="shouldRender"
    v-tooltip.top-end="tooltipText"
    icon="i-ph-whatsapp-logo"
    size="sm"
    slate
    faded
    :disabled="isBusy"
    :is-loading="isDialing"
    @click="onClick"
  />
</template>
